import { ENVS } from '@adsk/acs-sc-web-platform-api';

import safeParseObjectFromSessionStorage from './safeParseObjectFromSessionStorage';

export const DYNATRACE_OVERRIDE_SESSION_STORAGE_KEY =
  'ACS_EXO_DYNATRACE_OVERRIDES';

/**
 * Single source of truth for which environments allow the `disableDynatrace`
 * override. QA only today — to enable it elsewhere, change just this predicate
 * (both the agent and the dev overlay gate on it).
 */
export const isDynatraceOverrideAllowed = (env: keyof typeof ENVS): boolean =>
  env === ENVS.QA;

const getDisableDynatraceQueryValue = (): string | null =>
  new URLSearchParams(window.location.search).get('disableDynatrace');

/**
 * Pure read of the Dynatrace overrides: the `disableDynatrace` query param (if
 * present) merged over the persisted sessionStorage value. Does NOT write —
 * call {@link persistDynatraceOverride} once at bootstrap to persist the query
 * value, so reads stay side-effect-free (safe inside React render).
 */
export const getDynatraceOverrides = (): Record<string, string> => {
  const queryValue = getDisableDynatraceQueryValue();
  const fromSession = safeParseObjectFromSessionStorage(
    DYNATRACE_OVERRIDE_SESSION_STORAGE_KEY,
  ) as Record<string, string> | null;

  return queryValue !== null
    ? { ...fromSession, disableDynatrace: queryValue }
    : (fromSession ?? {});
};

/**
 * Persists the `disableDynatrace` query param into sessionStorage so the
 * override survives same-tab page loads. Call once at bootstrap (never during
 * render); it is a no-op when the query param is absent.
 */
export const persistDynatraceOverride = (): void => {
  if (getDisableDynatraceQueryValue() === null) {
    return;
  }

  sessionStorage.setItem(
    DYNATRACE_OVERRIDE_SESSION_STORAGE_KEY,
    JSON.stringify(getDynatraceOverrides()),
  );
};

export const isDynatraceDisabled = (): boolean =>
  getDynatraceOverrides()['disableDynatrace'] === 'true';

/**
 * Removes the `disableDynatrace` override from sessionStorage, leaving any
 * other keys under {@link DYNATRACE_OVERRIDE_SESSION_STORAGE_KEY} untouched.
 */
export const clearDynatraceOverride = (): void => {
  const fromSession = safeParseObjectFromSessionStorage(
    DYNATRACE_OVERRIDE_SESSION_STORAGE_KEY,
  ) as Record<string, string> | null;

  if (!fromSession) {
    return;
  }

  delete fromSession.disableDynatrace;

  sessionStorage.setItem(
    DYNATRACE_OVERRIDE_SESSION_STORAGE_KEY,
    JSON.stringify(fromSession),
  );
};
